import React, { useEffect, useState } from "react";
import { track } from "@vercel/analytics/react";
import type { LucideIcon } from "lucide-react";
import {
  Activity, ArrowLeftRight, ArrowRight, ArrowUpRight, Bell, Building2, CalendarCheck, Check,
  ClipboardList, Clapperboard, Download, FileText, Globe, HeartPulse, History, Images, Inbox,
  Layers, LayoutDashboard, Link2, Lock, Megaphone, Palette, PawPrint, QrCode, Route,
  Shapes, ShieldCheck, SlidersHorizontal, Smartphone, Sparkles, Stethoscope, Store,
  ToggleRight, UserCog, Users,
} from "lucide-react";
import { PAGES, PAGE_ORDER, PAWPAL_PATH } from "./content";
import type { Card, ImgSlot, PageData, PageId, Pair, Section, Side, Theme, Tone } from "./content";
import { IMAGES, SHOW_PLACEHOLDERS } from "./images";

// ── CONSTANTS ──────────────────────────────────────────────────────────────
const HEAD = "'Cabinet Grotesk',sans-serif";
const SERIF = "'Instrument Serif',serif";

const TONE: Record<Tone, { bg: string; sub: string }> = {
  light: { bg: "#f8f7f4", sub: "#555" },
  white: { bg: "#ffffff", sub: "#666" },
  dark: { bg: "#0e0e0e", sub: "rgba(255,255,255,.5)" },
};

const ICONS: Record<string, LucideIcon> = {
  Activity, Bell, Building2, CalendarCheck, ClipboardList, Clapperboard, Download, FileText,
  Globe, HeartPulse, History, Images, Inbox, Layers, LayoutDashboard, Link2, Lock, Megaphone,
  Palette, PawPrint, QrCode, Route, Shapes, ShieldCheck, SlidersHorizontal, Smartphone,
  Sparkles, Stethoscope, Store, ToggleRight, UserCog, Users,
};

function Icon({ name, size = 18, color }: { name?: string; size?: number; color?: string }) {
  const C = name ? ICONS[name] : undefined;
  return C ? <C size={size} color={color} strokeWidth={1.8} /> : null;
}

const pad2 = (n: number) => (n < 10 ? "0" : "") + n;

// ── NAVIGATION ─────────────────────────────────────────────────────────────
function goTo(path: string, from: string) {
  track("petling_link", { from, to: path });
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function Link({
  to, from, children, style, hover, external,
}: {
  to: string;
  from: string;
  children: React.ReactNode;
  style?: React.CSSProperties;
  hover?: React.CSSProperties;
  external?: boolean;
}) {
  const [h, setH] = useState(false);
  const merged: React.CSSProperties = {
    cursor: "none", textDecoration: "none", transition: "all .25s",
    ...style, ...(h && hover ? hover : {}),
  };
  if (external) {
    return (
      <a href={to} target="_blank" rel="noreferrer" data-hover style={merged}
        onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
        onClick={() => track("petling_external", { from, to })}>
        {children}
      </a>
    );
  }
  return (
    <a href={to} data-hover style={merged}
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
        e.preventDefault();
        goTo(to, from);
      }}>
      {children}
    </a>
  );
}

// Renders text with inline links written as [[/path|label]]
function RichText({ text, from, color }: { text: string; from: string; color: string }) {
  const parts = text.split(/(\[\[[^\]]+\]\])/g);
  return (
    <>
      {parts.map((part, i) => {
        const m = /^\[\[([^|\]]+)\|([^\]]+)\]\]$/.exec(part);
        if (m) {
          return (
            <Link key={i} to={m[1]} from={from}
              style={{ color, fontWeight: 600, borderBottom: `1.5px solid ${color}55` }}
              hover={{ borderBottomColor: color }}>
              {m[2]}
            </Link>
          );
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </>
  );
}

// ── STYLES ─────────────────────────────────────────────────────────────────
export function PetlingStyles() {
  return (
    <style>{`
      .pl-grid{display:grid;gap:18px;}
      .pl-cols-1{grid-template-columns:minmax(0,1fr);}
      .pl-cols-2{grid-template-columns:repeat(2,minmax(0,1fr));}
      .pl-cols-3{grid-template-columns:repeat(3,minmax(0,1fr));}
      .pl-cols-4{grid-template-columns:repeat(4,minmax(0,1fr));}
      .pl-card{transition:transform .25s cubic-bezier(.16,1,.3,1),box-shadow .25s;}
      .pl-card:hover{transform:translateY(-4px);}
      .pl-pair{display:grid;grid-template-columns:minmax(0,1fr) 64px minmax(0,1fr);align-items:stretch;}
      .pl-row{display:grid;grid-template-columns:200px minmax(0,1fr);gap:24px;}
      @media(max-width:900px){
        .pl-cols-3,.pl-cols-4{grid-template-columns:repeat(2,minmax(0,1fr));}
        .pl-pair{grid-template-columns:minmax(0,1fr);}
        .pl-connector{transform:rotate(90deg);padding:14px 0;}
        .pl-nav-switch{display:none !important;}
      }
      @media(max-width:600px){
        .pl-cols-2,.pl-cols-3,.pl-cols-4{grid-template-columns:minmax(0,1fr);}
        .pl-row{grid-template-columns:minmax(0,1fr);gap:6px;}
      }
    `}</style>
  );
}

// ── IMAGE SLOTS ────────────────────────────────────────────────────────────
function slotVisible(slot: ImgSlot): boolean {
  const entry = IMAGES[slot.id];
  return !!(entry && entry.url) || SHOW_PLACEHOLDERS;
}

function Slot({ slot, tone, theme, tall }: { slot: ImgSlot; tone: Tone; theme: Theme; tall?: boolean }) {
  const entry = IMAGES[slot.id];
  const url = entry ? entry.url : "";
  const dark = tone === "dark";

  if (url) {
    const caption = slot.caption ? (
      <figcaption style={{ fontSize: 11, marginTop: 10, textAlign: "center", color: dark ? "rgba(255,255,255,.4)" : "#999", fontWeight: 500, letterSpacing: 0.3 }}>
        {slot.caption}
      </figcaption>
    ) : null;

    // "tall": the image fills the full height of the text column beside it
    if (tall) {
      return (
        <figure style={{ margin: 0, height: "100%", display: "flex", flexDirection: "column" }}>
          <div style={{ flex: 1, minHeight: 380, position: "relative" }}>
            <img src={url} alt={slot.caption}
              style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center" }} />
          </div>
          {caption}
        </figure>
      );
    }

    return (
      <figure style={{ margin: 0 }}>
        <img src={url} alt={slot.caption} style={{ width: "100%", display: "block" }} />
        {caption}
      </figure>
    );
  }

  if (!SHOW_PLACEHOLDERS) return null;
  const accent = dark ? theme.accentOnDark : theme.accent;
  return (
    <figure style={{ margin: 0 }}>
      <div style={{
        aspectRatio: slot.ratio || "16 / 10", width: "100%", borderRadius: 20,
        border: `1.5px dashed ${accent}66`, background: `${accent}12`,
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        gap: 6, padding: 20, textAlign: "center",
      }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.6, textTransform: "uppercase", color: accent }}>Image to come</span>
        <span style={{ fontSize: 11, color: dark ? "rgba(255,255,255,.45)" : "#888", fontWeight: 400, maxWidth: 260, lineHeight: 1.5 }}>
          {(entry && entry.hint) || slot.caption}
        </span>
        <span style={{ fontSize: 9, color: dark ? "rgba(255,255,255,.25)" : "#bbb", fontFamily: "monospace" }}>{slot.id}</span>
      </div>
      {slot.caption && (
        <figcaption style={{ fontSize: 11, marginTop: 10, color: dark ? "rgba(255,255,255,.4)" : "#999", fontWeight: 500, letterSpacing: 0.3 }}>
          {slot.caption}
        </figcaption>
      )}
    </figure>
  );
}

function HeroImage({ slot, theme }: { slot: ImgSlot; theme: Theme }) {
  const entry = IMAGES[slot.id];
  const url = entry ? entry.url : "";
  const radius = "24px 24px 0 0";
  if (url) {
    return <img src={url} alt={slot.caption} style={{ width: "100%", maxWidth: 560, display: "block" }} />;
  }
  if (!SHOW_PLACEHOLDERS) return null;
  const accent = theme.heroDark ? theme.accentOnDark : theme.accent;
  return (
    <div style={{
      width: "100%", maxWidth: 560, aspectRatio: slot.ratio || "4 / 3", borderRadius: radius,
      border: `1.5px dashed ${accent}77`, borderBottom: "none", background: `${accent}14`,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, padding: 20, textAlign: "center",
    }}>
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.6, textTransform: "uppercase", color: accent }}>Hero image to come</span>
      <span style={{ fontSize: 11, color: theme.heroDark ? "rgba(255,255,255,.5)" : "#888", maxWidth: 260, lineHeight: 1.5 }}>
        {(entry && entry.hint) || slot.caption}
      </span>
      <span style={{ fontSize: 9, color: theme.heroDark ? "rgba(255,255,255,.3)" : "#bbb", fontFamily: "monospace" }}>{slot.id}</span>
    </div>
  );
}

// ── SHARED PIECES ──────────────────────────────────────────────────────────
function Shell({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return (
    <div className="cs-section-pad"
      style={{
        background: TONE[tone].bg, padding: "80px 60px",
        borderTop: tone === "white" ? "1px solid rgba(0,0,0,.06)" : undefined,
        borderBottom: tone === "white" ? "1px solid rgba(0,0,0,.06)" : undefined,
      }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>{children}</div>
    </div>
  );
}

function Label({ num, text, tone, theme }: { num: string; text: string; tone: Tone; theme: Theme }) {
  const acc = tone === "dark" ? theme.accentOnDark : theme.accent;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
      <div style={{ width: 32, height: 1.5, background: acc }} />
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase", color: acc }}>{num ? num + " — " + text : text}</span>
    </div>
  );
}

function H2({ title, italic, tone, theme, mb }: { title: string; italic: string; tone: Tone; theme: Theme; mb: number }) {
  const acc = tone === "dark" ? theme.accentOnDark : theme.accent;
  return (
    <h2 style={{ fontFamily: HEAD, fontWeight: 900, fontSize: "clamp(28px,4vw,48px)", letterSpacing: -1.5, lineHeight: 1, color: tone === "dark" ? "white" : "#0e0e0e", marginBottom: mb }}>
      {title}{" "}
      <em style={{ fontFamily: SERIF, fontStyle: "italic", fontWeight: 400, color: acc }}>{italic}</em>
    </h2>
  );
}

function Head({ num, s, theme }: { num: string; s: { label: string; tone: Tone; title: string; italic: string; intro?: string }; theme: Theme }) {
  return (
    <>
      <Label num={num} text={s.label} tone={s.tone} theme={theme} />
      <H2 title={s.title} italic={s.italic} tone={s.tone} theme={theme} mb={s.intro ? 14 : 48} />
      {s.intro && (
        <p style={{ fontSize: 14, color: TONE[s.tone].sub, lineHeight: 1.8, maxWidth: 620, marginBottom: 48, fontWeight: 300 }}>{s.intro}</p>
      )}
    </>
  );
}

function Pill({ children, theme, dark }: { children: React.ReactNode; theme: Theme; dark?: boolean }) {
  return (
    <span style={{
      fontSize: 9, fontWeight: 800, letterSpacing: 0.8, textTransform: "uppercase", padding: "3px 9px", borderRadius: 100, whiteSpace: "nowrap", flexShrink: 0,
      background: dark ? "rgba(255,255,255,.08)" : theme.pale, color: dark ? theme.accentOnDark : theme.accent,
    }}>{children}</span>
  );
}

// ── SECTIONS ───────────────────────────────────────────────────────────────
function OverviewSection({ s, num, theme, from }: { s: Extract<Section, { type: "overview" }>; num: string; theme: Theme; from: string }) {
  const showImg = slotVisible(s.image);
  const cardBg = s.tone === "white" ? "#f8f7f4" : "white";
  return (
    <Shell tone={s.tone}>
      <Label num={num} text={s.label} tone={s.tone} theme={theme} />
      <div className={showImg ? "cs-two-col" : undefined}
        style={{ display: "grid", gridTemplateColumns: showImg ? "1fr 1fr" : "1fr", gap: 80, alignItems: s.tallImage ? "stretch" : "center" }}>
        <div>
          <H2 title={s.title} italic={s.italic} tone={s.tone} theme={theme} mb={24} />
          {s.paragraphs.map((p, i) => (
            <p key={i} style={{ fontSize: 15, color: "#555", lineHeight: 1.85, fontWeight: 300, marginBottom: 18 }}><RichText text={p} from={from} color={theme.accent} /></p>
          ))}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
            {s.points.map((pt, i) => (
              <div key={i} style={{ display: "flex", gap: 12, padding: "14px 16px", background: cardBg, borderRadius: 12, border: "1px solid rgba(0,0,0,.07)", boxShadow: s.tone === "white" ? "none" : "0 2px 10px rgba(0,0,0,.04)" }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: theme.pale, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Icon name={pt.icon} size={17} color={theme.accent} />
                </div>
                <div>
                  <div style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 13, color: "#0e0e0e", marginBottom: 3 }}>{pt.title}</div>
                  <div style={{ fontSize: 11.5, color: "#666", lineHeight: 1.6, fontWeight: 300 }}>{pt.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        {showImg && <Slot slot={s.image} tone={s.tone} theme={theme} tall={s.tallImage} />}
      </div>
      {s.principle && (
        <div style={{ marginTop: 48, background: theme.pale, border: `1px solid ${theme.accent}22`, borderRadius: 16, padding: "24px 28px" }}>
          <p style={{ fontFamily: SERIF, fontStyle: "italic", fontSize: 17, lineHeight: 1.65, color: theme.deep }}>"{s.principle}"</p>
        </div>
      )}
    </Shell>
  );
}

function StepsSection({ s, num, theme, from }: { s: Extract<Section, { type: "steps" }>; num: string; theme: Theme; from: string }) {
  return (
    <Shell tone={s.tone}>
      <Head num={num} s={s} theme={theme} />
      <div style={{ position: "relative" }}>
        <div className="cs-hide-mobile"
          style={{ position: "absolute", top: 24, left: "9%", right: "9%", height: 2, background: `linear-gradient(90deg,${theme.accent},${theme.accentOnDark})`, borderRadius: 2, zIndex: 0 }} />
        <div className="cs-process-row" style={{ display: "flex", gap: 0 }}>
          {s.steps.map((st, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", flex: 1, padding: "0 10px" }}>
              <div style={{ width: 48, height: 48, borderRadius: "50%", background: theme.accent, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: HEAD, fontWeight: 900, fontSize: 14, color: "white", marginBottom: 16, flexShrink: 0, position: "relative", zIndex: 1 }}>{pad2(i + 1)}</div>
              <h4 style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 14, color: "#0e0e0e", marginBottom: 8 }}>{st.title}</h4>
              <p style={{ fontSize: 12, color: "#666", lineHeight: 1.7, fontWeight: 300 }}><RichText text={st.desc} from={from} color={theme.accent} /></p>
            </div>
          ))}
        </div>
      </div>
    </Shell>
  );
}

function CardView({ c, i, variant, tone, theme, from }: { c: Card; i: number; variant: "feature" | "decision" | "problem" | "takeaway"; tone: Tone; theme: Theme; from: string }) {
  const cardBg = tone === "white" ? "#f8f7f4" : "white";

  if (variant === "problem") {
    return (
      <div className="pl-card" style={{ background: "#161616", border: "1px solid rgba(255,255,255,.08)", borderTop: `3px solid ${theme.accentOnDark}`, borderRadius: 18, padding: "32px 28px" }}>
        <div style={{ fontFamily: HEAD, fontSize: 44, fontWeight: 900, color: "rgba(255,255,255,.05)", lineHeight: 1, marginBottom: 20, letterSpacing: -2 }}>{pad2(i + 1)}</div>
        <h3 style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 16, color: "white", marginBottom: 10, lineHeight: 1.3 }}>{c.title}</h3>
        <p style={{ fontSize: 13, color: "rgba(255,255,255,.5)", lineHeight: 1.75, fontWeight: 300 }}>{c.body}</p>
      </div>
    );
  }

  if (variant === "takeaway") {
    return (
      <div className="pl-card" style={{ background: cardBg, border: "1px solid rgba(0,0,0,.07)", borderLeft: `3px solid ${theme.accent}`, borderRadius: "0 16px 16px 0", padding: "28px 24px", boxShadow: tone === "white" ? "none" : "0 2px 16px rgba(0,0,0,.05)" }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: `${theme.accent}88`, letterSpacing: 1, marginBottom: 8 }}>{pad2(i + 1)}</div>
        <h3 style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 15, color: "#0e0e0e", marginBottom: 8, lineHeight: 1.3 }}>{c.title}</h3>
        <p style={{ fontSize: 13, color: "#555", lineHeight: 1.72, fontWeight: 300 }}>{c.body}</p>
      </div>
    );
  }

  if (variant === "decision") {
    return (
      <div className="pl-card" style={{ background: theme.pale, border: `1px solid ${theme.accent}22`, borderRadius: 18, padding: "28px 24px" }}>
        <h4 style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 15, color: theme.accent, marginBottom: 10, lineHeight: 1.3 }}>{c.title}</h4>
        <p style={{ fontSize: 13, color: "#444", lineHeight: 1.75, fontWeight: 300 }}>{c.body}</p>
      </div>
    );
  }

  // feature
  const targetPage = c.target ? PAGES[c.target] : null;
  return (
    <div className="pl-card" style={{ background: cardBg, border: "1px solid rgba(0,0,0,.07)", borderRadius: 18, padding: "26px 24px", boxShadow: tone === "white" ? "none" : "0 2px 16px rgba(0,0,0,.05)", display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div style={{ width: 40, height: 40, borderRadius: 11, background: theme.pale, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon name={c.icon} size={19} color={theme.accent} />
        </div>
        {c.tag && <Pill theme={theme}>{c.tag}</Pill>}
      </div>
      <h3 style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 15, color: "#0e0e0e", marginBottom: 8, lineHeight: 1.3 }}>{c.title}</h3>
      <p style={{ fontSize: 13, color: "#555", lineHeight: 1.72, fontWeight: 300, flex: 1 }}>{c.body}</p>
      {targetPage && (
        <Link to={targetPage.path} from={from}
          style={{ marginTop: 16, display: "inline-flex", alignItems: "center", gap: 6, fontFamily: HEAD, fontWeight: 800, fontSize: 12, color: theme.accent }}
          hover={{ gap: 10 }}>
          {targetPage.eco.name} <ArrowRight size={13} />
        </Link>
      )}
    </div>
  );
}

function CardsSection({ s, num, theme, from }: { s: Extract<Section, { type: "cards" }>; num: string; theme: Theme; from: string }) {
  return (
    <Shell tone={s.tone}>
      <Head num={num} s={s} theme={theme} />
      <div className={`pl-grid pl-cols-${s.columns}`}>
        {s.cards.map((c, i) => (
          <CardView key={i} c={c} i={i} variant={s.variant} tone={s.tone} theme={theme} from={from} />
        ))}
      </div>
    </Shell>
  );
}

function GallerySection({ s, num, theme }: { s: Extract<Section, { type: "gallery" }>; num: string; theme: Theme }) {
  return (
    <Shell tone={s.tone}>
      <Head num={num} s={s} theme={theme} />
      <div className={`pl-grid pl-cols-${s.columns}`} style={{ alignItems: "start", gap: 24 }}>
        {s.images.map(img => (slotVisible(img) ? <Slot key={img.id} slot={img} tone={s.tone} theme={theme} /> : null))}
      </div>
    </Shell>
  );
}

function RowsSection({ s, num, theme }: { s: Extract<Section, { type: "rows" }>; num: string; theme: Theme }) {
  const dark = s.tone === "dark";
  return (
    <Shell tone={s.tone}>
      <Head num={num} s={s} theme={theme} />
      <div>
        {s.rows.map((r, i) => (
          <div key={i} className="pl-row"
            style={{ padding: "18px 0", borderBottom: `1px solid ${dark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.08)"}`, alignItems: "baseline", borderTop: i === 0 ? `1px solid ${dark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.08)"}` : undefined }}>
            <span style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 12, letterSpacing: 1.2, textTransform: "uppercase", color: dark ? theme.accentOnDark : theme.accent }}>{r.label}</span>
            <p style={{ fontSize: 14, color: dark ? "rgba(255,255,255,.65)" : "#444", lineHeight: 1.75, fontWeight: 300 }}>{r.text}</p>
          </div>
        ))}
      </div>
    </Shell>
  );
}

function SideCard({ side, theme, primary }: { side: Side; theme: Theme; primary?: boolean }) {
  return (
    <div style={{
      background: "#161616", border: "1px solid rgba(255,255,255,.08)", borderRadius: 18, padding: "28px 26px",
      borderTop: primary ? `3px solid ${theme.accentOnDark}` : "3px solid rgba(255,255,255,.14)",
    }}>
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.6, textTransform: "uppercase", color: primary ? theme.accentOnDark : "rgba(255,255,255,.4)", marginBottom: 8 }}>{side.eyebrow}</div>
      <h4 style={{ fontFamily: HEAD, fontWeight: 900, fontSize: 19, color: "white", marginBottom: 18, letterSpacing: -0.4 }}>{side.name}</h4>
      <div style={{ display: "flex", flexDirection: "column", gap: 11 }}>
        {side.items.map((it, i) => (
          <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
            <span style={{ marginTop: 3, flexShrink: 0, display: "inline-flex" }}><Check size={13} color={theme.accentOnDark} strokeWidth={2.4} /></span>
            <span style={{ fontSize: 13, color: "rgba(255,255,255,.62)", lineHeight: 1.65, fontWeight: 300 }}>{it}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PairView({ p, theme, from }: { p: Pair; theme: Theme; from: string }) {
  const target = PAGES[p.target];
  return (
    <div style={{ marginBottom: 56 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <span style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 18, color: "white", letterSpacing: -0.3 }}>{p.title}</span>
        <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,.08)" }} />
      </div>
      <div className="pl-pair">
        <SideCard side={p.left} theme={theme} primary />
        <div className="pl-connector" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ width: 44, height: 44, borderRadius: "50%", border: `1.5px solid ${theme.accentOnDark}55`, background: `${theme.accentOnDark}14`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ArrowLeftRight size={18} color={theme.accentOnDark} />
          </div>
        </div>
        <SideCard side={p.right} theme={theme} />
      </div>
      <div style={{ marginTop: 20 }}>
        <Link to={target.path} from={from}
          style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "transparent", color: "rgba(255,255,255,.75)", padding: "10px 20px", borderRadius: 100, fontFamily: HEAD, fontWeight: 700, fontSize: 12, border: "1.5px solid rgba(255,255,255,.18)" }}
          hover={{ borderColor: theme.accentOnDark, color: theme.accentOnDark }}>
          {p.ctaLabel} <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}

function ConnectedSection({ s, num, theme, from }: { s: Extract<Section, { type: "connected" }>; num: string; theme: Theme; from: string }) {
  return (
    <Shell tone={s.tone}>
      <Head num={num} s={s} theme={theme} />
      {s.pairs.map((p, i) => <PairView key={i} p={p} theme={theme} from={from} />)}
    </Shell>
  );
}

function PaletteSection({ s, num, theme }: { s: Extract<Section, { type: "palette" }>; num: string; theme: Theme }) {
  const imgs = s.images.filter(slotVisible);
  return (
    <Shell tone={s.tone}>
      <Head num={num} s={s} theme={theme} />
      <div className="pl-grid pl-cols-3" style={{ marginBottom: 40 }}>
        {s.swatches.map((sw, i) => (
          <div key={i}>
            <div style={{ height: 150, borderRadius: 18, background: sw.hex, border: sw.light ? "1px solid rgba(255,255,255,.14)" : "1px solid rgba(255,255,255,.08)", marginBottom: 14 }} />
            <div style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 15, color: "white" }}>{sw.name}</div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,.4)", marginTop: 3 }}>
              <span style={{ fontFamily: "monospace" }}>{sw.hex}</span> · {sw.role}
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: imgs.length ? 48 : 0, maxWidth: 720 }}>
        {s.rules.map((r, i) => (
          <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <span style={{ marginTop: 3, flexShrink: 0, display: "inline-flex" }}><Check size={14} color={theme.accentOnDark} strokeWidth={2.4} /></span>
            <p style={{ fontSize: 14, color: "rgba(255,255,255,.6)", lineHeight: 1.7, fontWeight: 300 }}>{r}</p>
          </div>
        ))}
      </div>
      {imgs.length > 0 && (
        <div className={"pl-grid " + (imgs.length === 1 ? "pl-cols-3" : "pl-cols-2")} style={{ gap: 24, alignItems: "start" }}>
          {imgs.map(img => <Slot key={img.id} slot={img} tone="dark" theme={theme} />)}
        </div>
      )}
    </Shell>
  );
}

// ── ECOSYSTEM ──────────────────────────────────────────────────────────────
interface EcoItem { path: string; name: string; kind: string; blurb: string; status: string; }

function ecoItems(currentId: PageId | null): EcoItem[] {
  const items: EcoItem[] = [];
  for (let i = 0; i < PAGE_ORDER.length; i++) {
    const p = PAGES[PAGE_ORDER[i]];
    if (p.id !== currentId) items.push({ path: p.path, ...p.eco });
  }
  if (currentId !== null) {
    items.push({
      path: PAWPAL_PATH, name: "PawPal (Concept)", kind: "Figma · Where it began",
      blurb: "The original concept: research, personas and every screen, designed before a line of code.",
      status: "Case study",
    });
  }
  return items;
}

function EcoGrid({ items, theme, from }: { items: EcoItem[]; theme: Theme; from: string }) {
  return (
    <div className={`pl-grid pl-cols-${items.length >= 4 ? 4 : 3}`}>
      {items.map((it, i) => (
        <Link key={i} to={it.path} from={from}
          style={{ display: "flex", flexDirection: "column", background: "#f8f7f4", border: "1px solid rgba(0,0,0,.07)", borderRadius: 18, padding: "26px 22px", height: "100%" }}
          hover={{ transform: "translateY(-4px)", boxShadow: "0 14px 36px rgba(0,0,0,.08)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 8 }}>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.4, textTransform: "uppercase", color: "#999" }}>{it.kind}</span>
            <Pill theme={theme}>{it.status}</Pill>
          </div>
          <h3 style={{ fontFamily: HEAD, fontWeight: 900, fontSize: 18, color: "#0e0e0e", letterSpacing: -0.4, marginBottom: 8, lineHeight: 1.2 }}>{it.name}</h3>
          <p style={{ fontSize: 12.5, color: "#666", lineHeight: 1.7, fontWeight: 300, flex: 1 }}>{it.blurb}</p>
          <span style={{ marginTop: 16, display: "inline-flex", alignItems: "center", gap: 6, fontFamily: HEAD, fontWeight: 800, fontSize: 12, color: theme.accent }}>
            Open <ArrowUpRight size={14} />
          </span>
        </Link>
      ))}
    </div>
  );
}

function EcosystemSection({ currentId, theme, from }: { currentId: PageId; theme: Theme; from: string }) {
  return (
    <Shell tone="white">
      <Label num="" text="The Petling Ecosystem" tone="white" theme={theme} />
      <H2 title="One product," italic="four surfaces." tone="white" theme={theme} mb={14} />
      <p style={{ fontSize: 14, color: "#666", lineHeight: 1.8, maxWidth: 620, marginBottom: 40, fontWeight: 300 }}>
        The mobile app, the admin console and the provider desk share one data model, and one brand. Follow the story across all of them.
      </p>
      <EcoGrid items={ecoItems(currentId)} theme={theme} from={from} />
    </Shell>
  );
}

// Used at the end of the PawPal concept case study
export function PetlingContinuation() {
  const theme = PAGES.app.theme;
  return (
    <>
      <PetlingStyles />
      <div className="cs-section-pad" style={{ background: "#ffffff", padding: "80px 60px", borderTop: "1px solid rgba(0,0,0,.06)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <div style={{ width: 32, height: 1.5, background: theme.accent }} />
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase", color: theme.accent }}>The Story Continues</span>
          </div>
          <H2 title="PawPal became" italic="Petling." tone="white" theme={theme} mb={14} />
          <p style={{ fontSize: 14, color: "#666", lineHeight: 1.8, maxWidth: 620, marginBottom: 40, fontWeight: 300 }}>
            The concept was only the beginning. It grew into a live app on both stores, an admin console to run it, a desk for daycare and boarding teams, and a website and brand around all of it.
          </p>
          <EcoGrid items={ecoItems(null)} theme={theme} from="pawpal" />
        </div>
      </div>
    </>
  );
}

// ── PAGE ───────────────────────────────────────────────────────────────────
function sectionVisible(s: Section): boolean {
  if (s.type === "overview") return true; // text always shows
  if (s.type === "gallery") return s.images.some(slotVisible);
  return true;
}

export function PetlingSuitePage({ page }: { page: PageData }) {
  const { theme } = page;
  const dark = theme.heroDark;
  const next = PAGES[page.next];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page.id]);

  const onBack = () => {
    window.history.pushState({}, "", "/");
    window.dispatchEvent(new PopStateEvent("popstate"));
  };

  const visible = page.sections.filter(sectionVisible);

  const renderSection = (s: Section, idx: number) => {
    const num = pad2(idx + 1);
    switch (s.type) {
      case "overview": return <OverviewSection key={idx} s={s} num={num} theme={theme} from={page.id} />;
      case "steps": return <StepsSection key={idx} s={s} num={num} theme={theme} from={page.id} />;
      case "cards": return <CardsSection key={idx} s={s} num={num} theme={theme} from={page.id} />;
      case "gallery": return <GallerySection key={idx} s={s} num={num} theme={theme} />;
      case "connected": return <ConnectedSection key={idx} s={s} num={num} theme={theme} from={page.id} />;
      case "rows": return <RowsSection key={idx} s={s} num={num} theme={theme} />;
      case "palette": return <PaletteSection key={idx} s={s} num={num} theme={theme} />;
      default: return null;
    }
  };

  const heroAccent = dark ? theme.accentOnDark : theme.accent;
  const barBg = dark ? theme.heroBg + "f7" : "rgba(248,247,244,.97)";
  const barText = dark ? "rgba(255,255,255,.6)" : "#555";
  const barTextHover = dark ? "white" : "#0e0e0e";

  return (
    <div style={{ background: "#f8f7f4", minHeight: "100vh" }}>
      <PetlingStyles />

      {/* ── STICKY TOP BAR ── */}
      <div className="cs-topbar-pad"
        style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000, background: barBg, backdropFilter: "blur(14px)", borderBottom: dark ? "1px solid rgba(255,255,255,.08)" : "1px solid rgba(0,0,0,.06)", height: 64, display: "flex", alignItems: "center", padding: "0 60px", justifyContent: "space-between", gap: 16 }}>
        <button onClick={onBack} data-hover
          style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "none", fontFamily: HEAD, fontWeight: 700, fontSize: 13, color: barText, padding: 0, transition: "color .2s", whiteSpace: "nowrap" }}
          onMouseEnter={e => ((e.currentTarget as HTMLButtonElement).style.color = barTextHover)}
          onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.color = barText)}>
          ← Back to Portfolio
        </button>
        <span style={{ fontFamily: HEAD, fontWeight: 900, fontSize: 16, letterSpacing: -0.5, color: dark ? "white" : "#0e0e0e" }}>
          Lobna<span style={{ color: dark ? theme.accentOnDark : "#7dd4c0" }}>.</span>
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <div className="pl-nav-switch" style={{ display: "flex", alignItems: "center", gap: 4, marginRight: 8 }}>
            {PAGE_ORDER.map(id => {
              const p = PAGES[id];
              const current = id === page.id;
              return (
                <Link key={id} to={p.path} from={page.id}
                  style={{
                    padding: "7px 13px", borderRadius: 100, fontFamily: HEAD, fontWeight: 700, fontSize: 12,
                    background: current ? (dark ? theme.accentOnDark : theme.accent) : "transparent",
                    color: current ? (dark ? "#0e0e0e" : "white") : barText,
                  }}
                  hover={current ? undefined : { color: barTextHover }}>
                  {p.shortName}
                </Link>
              );
            })}
          </div>
          {page.cta && (
            <Link to={page.cta.href} from={page.id} external
              style={{ background: dark ? theme.accentOnDark : "#0e0e0e", color: dark ? "#0e0e0e" : "white", padding: "9px 20px", borderRadius: 100, fontFamily: HEAD, fontWeight: 800, fontSize: 12, display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
              hover={{ background: dark ? "white" : theme.accent }}>
              {page.cta.label} <ArrowUpRight size={13} />
            </Link>
          )}
        </div>
      </div>

      {/* ── HERO ── */}
      <div style={{ paddingTop: 64, background: theme.heroBg, borderBottom: dark ? "none" : "1px solid rgba(0,0,0,.06)", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -80, right: -80, width: 400, height: 400, background: theme.glow, borderRadius: "50%", filter: "blur(100px)", opacity: dark ? 0.14 : 0.18, pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -60, left: -60, width: 300, height: 300, background: dark ? theme.accentOnDark : theme.accent, borderRadius: "50%", filter: "blur(80px)", opacity: dark ? 0.08 : 0.1, pointerEvents: "none" }} />

        <div className="cs-section-pad" style={{ maxWidth: 1200, margin: "0 auto", padding: "80px 60px 0", position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 28 }}>
            <div style={{ background: dark ? "rgba(255,255,255,.06)" : theme.pale, border: `1px solid ${heroAccent}55`, borderRadius: 100, padding: "5px 14px", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 6, height: 6, background: heroAccent, borderRadius: "50%" }} />
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: heroAccent }}>{page.badge}</span>
            </div>
          </div>

          <div className="cs-hero-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 60, alignItems: "flex-end" }}>
            <div>
              <h1 style={{ fontFamily: HEAD, fontWeight: 900, fontSize: "clamp(52px,7.5vw,96px)", letterSpacing: -4, lineHeight: 0.88, color: dark ? "white" : "#0e0e0e" }}>{page.name}</h1>
              <h1 style={{ fontFamily: SERIF, fontStyle: "italic", fontWeight: 400, fontSize: "clamp(38px,5.4vw,70px)", letterSpacing: -2, lineHeight: 0.98, color: heroAccent, marginBottom: 24 }}>{page.italic}</h1>
              <p style={{ fontSize: 17, color: dark ? "rgba(255,255,255,.65)" : "#555", lineHeight: 1.75, fontWeight: 300, maxWidth: 500, marginBottom: 32 }}>{page.tagline}</p>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 44 }}>
                {page.tags.map((t, i) => (
                  <span key={i} style={{
                    background: i === 0 ? (dark ? theme.accentOnDark : theme.accent) : "transparent",
                    color: i === 0 ? (dark ? "#0e0e0e" : "white") : heroAccent,
                    border: "1.5px solid", borderColor: i === 0 ? (dark ? theme.accentOnDark : theme.accent) : `${heroAccent}66`,
                    borderRadius: 100, padding: "6px 16px", fontSize: 11, fontWeight: 700, fontFamily: HEAD, letterSpacing: 0.3,
                  }}>{t}</span>
                ))}
              </div>
              <div className="cs-four-col" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 24, paddingTop: 32, paddingBottom: 48, borderTop: dark ? "1px solid rgba(255,255,255,.1)" : "1px solid rgba(0,0,0,.08)" }}>
                {page.meta.map((m, i) => (
                  <div key={i}>
                    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: dark ? "rgba(255,255,255,.35)" : "#999", marginBottom: 4 }}>{m.label}</div>
                    <div style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 13, color: dark ? "white" : "#0e0e0e" }}>{m.val}</div>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-end" }}>
              <HeroImage slot={page.heroImage} theme={theme} />
            </div>
          </div>
        </div>
      </div>

      {/* ── BAND ── */}
      <div className="cs-section-pad" style={{ background: dark ? "#0e0e0e" : theme.deep, padding: "48px 60px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", flexWrap: "wrap", gap: 32, alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ maxWidth: 520 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: theme.accentOnDark, marginBottom: 8 }}>{page.band.eyebrow}</div>
            <h3 style={{ fontFamily: HEAD, fontWeight: 800, fontSize: 20, color: "white", lineHeight: 1.35 }}>{page.band.text}</h3>
          </div>
          <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
            {page.band.stats.map((st, i) => (
              <div key={i}>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)", marginBottom: 4 }}>{st.label}</div>
                <div style={{ fontFamily: HEAD, fontWeight: 900, fontSize: 17, color: theme.accentOnDark }}>{st.val}</div>
              </div>
            ))}
          </div>
          {page.band.link && (
            <Link to={page.band.link.to} from={page.id}
              style={{ background: theme.accentOnDark, color: "#0e0e0e", padding: "12px 26px", borderRadius: 100, fontFamily: HEAD, fontWeight: 800, fontSize: 13, display: "inline-flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}
              hover={{ transform: "translateY(-2px)" }}>
              {page.band.link.label} <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </div>

      {/* ── SECTIONS ── */}
      {visible.map(renderSection)}

      <EcosystemSection currentId={page.id} theme={theme} from={page.id} />

      {/* ── CTA ── */}
      <div className="cs-section-pad" style={{ background: "#0e0e0e", padding: "80px 60px", textAlign: "center" }}>
        <div style={{ maxWidth: 620, margin: "0 auto" }}>
          <div style={{ fontFamily: HEAD, fontWeight: 900, fontSize: "clamp(34px,5vw,44px)", color: "#f8f7f4", letterSpacing: -2, lineHeight: 0.98, marginBottom: 16 }}>
            {page.closing.title}<br />
            <em style={{ fontFamily: SERIF, fontStyle: "italic", fontWeight: 400, color: theme.accentOnDark }}>{page.closing.italic}</em>
          </div>
          <p style={{ fontSize: 14, color: "rgba(255,255,255,.45)", lineHeight: 1.8, marginBottom: 32, fontWeight: 300 }}>{page.closing.text}</p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            {page.cta && (
              <Link to={page.cta.href} from={page.id} external
                style={{ background: theme.accentOnDark, color: "#0e0e0e", padding: "14px 32px", borderRadius: 100, fontFamily: HEAD, fontWeight: 800, fontSize: 14, display: "inline-flex", alignItems: "center", gap: 8 }}
                hover={{ transform: "translateY(-3px)", boxShadow: `0 12px 36px ${theme.accentOnDark}66` }}>
                {page.cta.label} <ArrowUpRight size={15} />
              </Link>
            )}
            <Link to={next.path} from={page.id}
              style={page.cta
                ? { background: "transparent", color: "rgba(255,255,255,.7)", padding: "14px 32px", borderRadius: 100, fontFamily: HEAD, fontWeight: 700, fontSize: 14, border: "1.5px solid rgba(255,255,255,.18)", display: "inline-flex", alignItems: "center", gap: 8 }
                : { background: theme.accentOnDark, color: "#0e0e0e", padding: "14px 32px", borderRadius: 100, fontFamily: HEAD, fontWeight: 800, fontSize: 14, display: "inline-flex", alignItems: "center", gap: 8 }}
              hover={page.cta ? { borderColor: "rgba(255,255,255,.4)", color: "white" } : { transform: "translateY(-3px)", boxShadow: `0 12px 36px ${theme.accentOnDark}66` }}>
              Next: {next.eco.name} <ArrowRight size={15} />
            </Link>
            <button onClick={onBack} data-hover
              style={{ background: "transparent", color: "rgba(255,255,255,.55)", padding: "14px 32px", borderRadius: 100, fontFamily: HEAD, fontWeight: 700, fontSize: 14, border: "1.5px solid rgba(255,255,255,.18)", cursor: "none", transition: "all .25s" }}
              onMouseEnter={e => { const b = e.currentTarget as HTMLButtonElement; b.style.borderColor = "rgba(255,255,255,.4)"; b.style.color = "white"; }}
              onMouseLeave={e => { const b = e.currentTarget as HTMLButtonElement; b.style.borderColor = "rgba(255,255,255,.18)"; b.style.color = "rgba(255,255,255,.55)"; }}>
              ← Back to Portfolio
            </button>
          </div>
        </div>
      </div>

      <div className="cs-topbar-pad" style={{ background: "#0a0a0a", padding: "24px 60px", display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: "1px solid rgba(255,255,255,.05)" }}>
        <span style={{ fontFamily: HEAD, fontWeight: 900, fontSize: 14, color: "white", letterSpacing: -0.3 }}>Lobna<span style={{ color: theme.accentOnDark }}>.</span></span>
        <span style={{ fontSize: 12, color: "rgba(255,255,255,.25)" }}>{page.footerNote}</span>
      </div>
    </div>
  );
}
